import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  CampaignResponse,
  CampaignSummary,
  CreateCampaignRequest,
  CreateCampaignResponse,
  GetAllCampaignRequest,
  UpdateCampaignRequest,
} from 'redux/types/Campaign.types';

export interface CampaignState {
  data: CreateCampaignRequest;
  campaignSummary: CampaignSummary;
  allCampaigns: CampaignResponse;
  loading: boolean;
  isCampaignSummaryLoading: boolean;
  isCampaignListLoading: boolean;
  isCampaignCreateLoading: boolean;
  isCampaignUpdateLoading: boolean;
  error: string | null;
  success: boolean;
  startCampaignStatus: boolean | null;
  pauseCampaignStatus: boolean | null;
}

export const campaignInitialState: CampaignState = {
  data: {
    name: '',
    days: [],
    dids: [],
    ratio: 0,
    type: '',
    ringTime: 0,
    autoDisposeTimer: 90,
    isAutoChurn: false,
    autoChurnCount: 0,
    isIntensityEnabled: false,
    intensityCount: 0,
  },
  campaignSummary: {
    totalAgents: 0,
    onlineAgents: 0,
    capacityUtilisation: 0,
    unusedCapacity: 0,
    unallocatedAgents: 0,
    nilAllocatedAgents: 0,
    totalCampaigns: 0,
    activeCampaigns: 0,
  },
  allCampaigns: {
    data: null,
    totalRecords: 0,
    pagination: null,
  },
  loading: false,
  isCampaignSummaryLoading: false,
  isCampaignListLoading: false,
  isCampaignCreateLoading: false,
  isCampaignUpdateLoading: false,
  error: null,
  success: false,
  startCampaignStatus: null,
  pauseCampaignStatus: null,
};

const campaignSlice = createSlice({
  name: 'campaign',
  initialState: campaignInitialState,
  reducers: {
    createCampaign: (state, action: PayloadAction<CreateCampaignRequest>) => {
      state.isCampaignCreateLoading = true;
      state.error = null;
    },
    createCampaignSuccess: (
      state,
      action: PayloadAction<CreateCampaignResponse>
    ) => {
      state.success = true;
      state.isCampaignCreateLoading = false;
    },
    createCampaignFailure: (state, action: PayloadAction<string>) => {
      state.isCampaignCreateLoading = false;
      state.error = action.payload;
    },
    updateCampaign: (state, action: PayloadAction<UpdateCampaignRequest>) => {
      state.isCampaignUpdateLoading = true;
      state.error = null;
    },
    updateCampaignSuccess: (
      state,
      action: PayloadAction<CreateCampaignResponse>
    ) => {
      state.success = true;
      state.isCampaignUpdateLoading = false;
    },
    updateCampaignFailure: (state, action: PayloadAction<string>) => {
      state.isCampaignUpdateLoading = false;
      state.error = action.payload;
    },
    fetchCampaignSummary: (state) => {
      state.isCampaignSummaryLoading = true;
      state.error = null;
    },
    fetchCampaignSummarySuccess: (
      state,
      action: PayloadAction<CampaignSummary>
    ) => {
      state.campaignSummary = action.payload;
      state.isCampaignSummaryLoading = false;
    },
    fetchCampaignSummaryFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isCampaignSummaryLoading = false;
    },
    fetchAllCampaigns: (
      state,
      action: PayloadAction<GetAllCampaignRequest>
    ) => {
      state.isCampaignListLoading = true;
      state.error = null;
    },
    fetchAllCampaignsSuccess: (
      state,
      action: PayloadAction<CampaignResponse>
    ) => {
      state.allCampaigns = action.payload;
      state.isCampaignListLoading = false;
    },
    fetchAllCampaignsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isCampaignListLoading = false;
    },
    startCampaigns: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.startCampaignStatus = null;
    },
    startCampaignsSuccess: (state) => {
      state.startCampaignStatus = true;
      state.loading = false;
    },
    startCampaignsFailure: (state) => {
      state.startCampaignStatus = false;
      state.loading = false;
    },
    pauseCampaigns: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.pauseCampaignStatus = null;
    },
    pauseCampaignsSuccess: (state) => {
      state.pauseCampaignStatus = true;
      state.loading = false;
    },
    pauseCampaignsFailure: (state) => {
      state.pauseCampaignStatus = false;
      state.loading = false;
    },
    resetCampaign: () => campaignInitialState,
    resetStartPauseStatus: (status) => {
      status.startCampaignStatus = null;
      status.pauseCampaignStatus = null;
    },
  },
});

export const {
  createCampaign,
  createCampaignSuccess,
  createCampaignFailure,
  updateCampaign,
  updateCampaignSuccess,
  updateCampaignFailure,
  resetCampaign,
  fetchCampaignSummary,
  fetchCampaignSummarySuccess,
  fetchCampaignSummaryFailure,
  fetchAllCampaigns,
  fetchAllCampaignsSuccess,
  fetchAllCampaignsFailure,
  startCampaigns,
  startCampaignsSuccess,
  startCampaignsFailure,
  pauseCampaigns,
  pauseCampaignsSuccess,
  pauseCampaignsFailure,
  resetStartPauseStatus,
} = campaignSlice.actions;

export default campaignSlice.reducer;
